/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';

import { FeatureState } from '@msdyn365-commerce/retail-proxy';
import { getActivePricesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import {
    AffiliationLoyaltyTier,
    ProductPrice,
    ProductType,
    ProjectionDomain
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import {
    getSelectedProductIdFromActionInput,
    SelectedVariantInput,
    QueryResultSettingsProxy,
    ArrayExtensions,
    ISelectedProduct
} from '@msdyn365-commerce-modules/retail-actions';
import { FeatureStateInput, getFeatureStateActionDataAction } from './get-feature-state';
import { getAffiliation } from '../shared/functions/get-affiliation';
import getSelectedVariant from './get-selected-variant';

/**
 * Input class for the GetPriceForSelectedVariant Data Action.
 */
export class PriceForSelectedVariantInput implements Msdyn365.IActionInput {
    public productId: number;

    public channelId: number;

    public selectedProduct: ISelectedProduct | undefined;

    public customerId?: string;

    public constructor(productId: number, channelId: number, selectedProduct?: ISelectedProduct, customerId?: string) {
        this.productId = productId;
        this.channelId = channelId;
        this.selectedProduct = selectedProduct;
        this.customerId = customerId ?? '';
    }

    public getCacheKey = (): string => 'PriceForSelectedVariant';

    public getCacheObjectType = (): string => 'Price';

    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

/**
 * The createInput method for the GetPriceForSelectedVariantDataAction.
 * @param inputData - The input data for the createInput method.
 * @returns The PriceForSelectedVariantInput.
 */
export const createActivePriceForSelectedVariantInput = (
    inputData: Msdyn365.ICreateActionContext<Msdyn365.IGeneric<Msdyn365.IAny>>
): PriceForSelectedVariantInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);

    if (productId) {
        return new PriceForSelectedVariantInput(+productId, +inputData.requestContext.apiSettings.channelId, undefined);
    }
    throw new Error('Unable to create PriceForSelectedVariantInput, no productId found on module config or query');
};

/**
 * The Action Method for the GetPriceForSelectedVariant Data Action
 * Pulls the currently selected variant from the cache using the getSelectedVariant data action, and gets it's current contextual price
 * via the getActivePrice RetailServer API.
 * @param input - The input.
 * @param ctx - The ctx.
 * @returns The Promise<ProductPrice | null>.
 */
export async function getPriceForSelectedVariantAction(
    input: PriceForSelectedVariantInput,
    ctx: Msdyn365.IActionContext
): Promise<ProductPrice | null> {
    return Promise.resolve()
        .then(() => {
            const activeProduct: ISelectedProduct | undefined = input.selectedProduct;

            if (!activeProduct) {
                const selectedVariantInput = new SelectedVariantInput(
                    input.productId,
                    input.channelId,
                    undefined,
                    undefined,
                    ctx.requestContext
                );

                return getSelectedVariant(selectedVariantInput, ctx);
            }

            return activeProduct;
        })
        .then<ProductPrice | null>(async (productResult: ISelectedProduct | null) => {
            const catalogId = Msdyn365.getCatalogId(ctx.requestContext);
            const projectDomain: ProjectionDomain = {
                ChannelId: +ctx.requestContext.apiSettings.channelId,
                CatalogId: catalogId
            };

            var affiliation: AffiliationLoyaltyTier[] = getAffiliation(
                ctx.requestContext.locale,
                ctx.requestContext.app.config.affiliationIds,
                ctx.requestContext.app.config.loyaltyTierIds
            );

            const activeProduct: ISelectedProduct | undefined = productResult as ISelectedProduct | undefined;
            if (activeProduct) {
                const featureStates: FeatureState[] = await getFeatureStateActionDataAction(new FeatureStateInput(), ctx);
                return getActivePricesAsync(
                    { callerContext: ctx, queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(ctx) },
                    projectDomain,
                    [activeProduct.productVariant ? activeProduct.productVariant.RecordId : activeProduct.RecordId],
                    new Date(),
                    input.customerId,
                    affiliation,
                    true,
                    featureStates.find(featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature')
                        ?.IsEnabled &&
                        (activeProduct.ProductTypeValue === ProductType.Master || activeProduct.ProductTypeValue === ProductType.KitMaster),
                    featureStates.find(
                        featureState => featureState.Name === 'Dynamics.AX.Application.RetailSalesAgreementPriceSupportFeature'
                    )?.IsEnabled
                ).then((productPrices: ProductPrice[]) => {
                    if (!ArrayExtensions.hasElements(productPrices)) {
                        throw new Error('[getPriceForSelectedVariantAction]Invalid response recieved from getActivePricesAsync');
                    }
                    if (typeof window !== 'undefined') {
                        window.dispatchEvent(
                            new CustomEvent('datalayerEntityLoaded', {
                                detail: {
                                    typeName: 'PRICE',
                                    key: 'PRICEFORSELECTEDVARIANT',
                                    result: productPrices[0]
                                }
                            })
                        );
                    }
                    return productPrices[0];
                });
            }

            return null;
        })
        .catch((error: Error) => {
            ctx.trace(error.message);
            ctx.telemetry.exception(error);
            ctx.telemetry.debug('[getPriceForSelectedVariantAction]Error executing action');
            throw new Error('[getPriceForSelectedVariantAction]Error executing action');
        });
}

export default Msdyn365.createObservableDataAction({
    action: getPriceForSelectedVariantAction as Msdyn365.IAction<ProductPrice | null>,
    id: '@msdyn365-commerce-modules/retail-actions/get-price-for-selected-variant',
    input: createActivePriceForSelectedVariantInput
});
